import { Permission } from '@Constants/Permission.g';
import { useHasPermission } from '@Contexts/appContext';
import { useContainerStock, useContainerTypeDetails, usePlannedAtSite } from '@Modules/Orders/Editing/Containers';
import { type LinkableField } from '@synqo/frontend-component-library';
import { useMemo } from 'react';
import { toPlannedLines, toStockLines } from './containerOrder';
import { type ContainerStockView } from './steps';

export interface SiteStockRequest {
    siteId: string | undefined;
    customerId: string | undefined;
    containerTypeOf: (containerType: LinkableField | undefined) => string;
}

export interface SiteStock {
    stock: ContainerStockView;
    /** How many containers stand at the site. */
    total: number;
    /** The container types of all lines, for looking up their names. */
    containerTypes: LinkableField[];
}

/** The containers standing at the site and those open orders plan to place there. */
// @ai-generated 2026-10-21T11:23:08+00:00 by niklas.eicker@tngtech.com using Claude Opus 5.5 (1M context)
export function useSiteStock({ siteId, customerId, containerTypeOf }: SiteStockRequest): SiteStock {
    const stockState = useContainerStock({ siteId, customerId, bookedContainers: 0 });
    const planned = usePlannedAtSite({ siteId, customerId, savedAt: '' });
    const isPlannedLoading = !!siteId && !!customerId && !planned.loaded;

    const { entries } = stockState;
    const { entries: plannedEntries } = planned;

    const hasPermission = useHasPermission();
    const canReadContainerTypes = hasPermission(Permission.StammdatenContainerTypeRead);
    const plannedTypeIds = useMemo(
        () => (canReadContainerTypes ? plannedEntries.map((entry) => entry.container_type.foreign_key) : []),
        [canReadContainerTypes, plannedEntries],
    );
    const management = useContainerTypeDetails(plannedTypeIds);

    const lines = useMemo(
        () => [
            ...toStockLines(entries, containerTypeOf),
            ...toPlannedLines(plannedEntries, containerTypeOf, management),
        ],
        [entries, plannedEntries, containerTypeOf, management],
    );

    const containerTypes = useMemo(
        () => [...entries, ...plannedEntries].map((entry) => entry.container_type),
        [entries, plannedEntries],
    );

    const stock = useMemo<ContainerStockView>(
        () => ({
            lines,
            isLoading: stockState.isLoading || isPlannedLoading,
            failure: stockState.failure,
            reload: stockState.reload,
        }),
        [lines, stockState.isLoading, isPlannedLoading, stockState.failure, stockState.reload],
    );

    return { stock, total: stockState.total, containerTypes };
}
